import { Button, HSpacer, NumericInput, Text, VSpacer } from '@/components/DesignSystem';
import { Routes } from '@/constants/Routes';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { AuthApi } from '@/utilities/api/AuthApi';
import { DetailedApiError } from '@/utilities/api/DetailedApiError';
import { UserEndpoint } from '@api/endpoints';
import { Stack, useTheme } from '@mui/material';
import { ApiErrors } from '@shared/enums';
import React, { KeyboardEvent, RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

interface OtpFormProps {
  lastFour: string,
  onCancel?: () => void,
  onError?: (error: DetailedApiError) => void,
  sendOtp?: boolean,
  userToCreate?: Omit<UserEndpoint.Create.FarmerUserCreate, 'code'>,
  telephone: string,
}

const OtpForm = ({
  lastFour,
  onCancel,
  onError,
  sendOtp = true,
  telephone,
}: OtpFormProps) => {
  const [otpValues, setOtpValues] = useState<(number | undefined)[]>([
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
  ]);
  const otpFieldsRef: RefObject<(HTMLDivElement | null)[]> = useRef([]);
  const [errorMessage, setErrorMessage] = useState<{ title?: string, message: string }>();
  const { refreshUser, validateOtp } = useAuthentication();
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [otpResendCounter, setOtpResendCounter] = useState(0);
  const [hideResend, setHideResend] = useState(false);

  useEffect(() => {
    if (sendOtp) {
      sendOtpCode();
    } else {
      setOtpResendCounter(30);
    }
    resetForm();
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timer;
    if (otpResendCounter > 0) {
      timer = setInterval(() => setOtpResendCounter(otpResendCounter - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [otpResendCounter]);

  const resetForm = () => {
    setTimeout(() => {
      otpFieldsRef?.current?.[0]?.focus();
    }, 100);
    setOtpValues([undefined, undefined, undefined, undefined, undefined, undefined]);
  };

  const { mutate: authenticateUser, isLoading: isAuthenticating } = useMutation(
    ({ otpValue }: { otpValue: string }) => (
      validateOtp(telephone, otpValue)
    ),
    {
      onError: (err: { message: string }) => {
        if (err.message === ApiErrors.inactiveAccount) {
          setErrorMessage({ title: t($ => $.auth.errors.inactiveAccount),  message: err.message });
        } else {
          setErrorMessage({ message: t($ => $.auth.otp.incorrectOtp) });
        }
        resetForm();
      },
      onSuccess: async () => {
        await refreshUser();
        navigate(Routes.HOMEPAGE, { replace: true });
      },
      retry: false,
    },
  );

  const { mutate: sendOtpCode, isLoading: isSendingOtp } = useMutation(
    () => AuthApi.sendOtpCode({ telephone: telephone.replace(/[() -]/g, "") }),
    {
      onError: (err: DetailedApiError) => {
        setErrorMessage({ message: err.message });
        if (
          err.message === ApiErrors.userWithPhoneExists
          || err.message === ApiErrors.userWithPhoneDoesNotExist
        ) {
          setHideResend(true);
        }
        onError?.(err);
      },
      onSuccess: () => {
        setErrorMessage(undefined);
        setOtpResendCounter(30);
      },
      retry: false,
    },
  );

  const handleKeyboardEvent = async (
    event: KeyboardEvent,
    index: number,
  ) => {
    if (event.key === "Backspace" || event.key === "Delete") {
      const isFirstInput = index === 0;
      if (!isFirstInput && otpFieldsRef?.current) {
        otpFieldsRef.current[index - 1]?.focus();
      }
    } else if (isFinite(Number(event.key))) {
      const newOtpValues = [...otpValues];
      newOtpValues[index] = Number(event.key);
      setOtpValues(newOtpValues);
      const isLastInput = (index + 1) === newOtpValues.length;
      if (isLastInput) {
        const otpValue = newOtpValues.join('');
        authenticateUser({ otpValue });
      } else {
        if (otpFieldsRef?.current) {
          otpFieldsRef.current[index + 1]?.focus();
        }
      }
    }
  };

  return (
    <Stack alignItems="center" flexDirection="column">
      <Text category="headline-large">{t($ => $.auth.otp.title)}</Text>
      <VSpacer size="3" />
      <Text category="body-large" sx={{ color: theme.palette.text.secondary }}>
        {t($ => $.auth.otp.instruction, { lastFour })}
      </Text>
      <VSpacer size="7" />
      <VSpacer size="2" />
      {!!errorMessage && (
        <>
          <Text category="body-medium" color="error">
            {errorMessage.message}
          </Text>
          <VSpacer size="6" />
        </>
      )}
      {!hideResend &&
        <>
          <Stack flexDirection="row">
            {otpValues.map((value, index) => (
              <Stack flexDirection="row" key={index}>
                <NumericInput
                  decimals={0}
                  disabled={isAuthenticating || isSendingOtp}
                  error={!!errorMessage}
                  hiddenLabel
                  inputRef={(ref) => {
                    if (otpFieldsRef.current) {
                      otpFieldsRef.current[index] = ref;
                    }
                  }}
                  maxLength={1}
                  maxValue={9}
                  minValue={0}
                  onKeyUp={(event) => handleKeyboardEvent(event, index)}
                  showFixedDecimals={false}
                  sx={{ height: '56px', p: 0, textAlign: 'center', width: '56px' }}
                  testID={`${index}-otp-input`}
                  value={value}
                  variant="outlined"
                />
                <HSpacer size="3" />
              </Stack>
            ))}
          </Stack>
        </>
      }
      {!isSendingOtp && otpResendCounter > 0 && !hideResend &&
        <>
          <VSpacer size="7" />
          <Text category="p2">
            {t($ => $.auth.otp.resendCounter, { count: otpResendCounter })}
          </Text>
        </>
      }
      <VSpacer size="8" />
      <Stack direction="row">
        <Button
          onClick={onCancel}
          testID="otp-form-cancel-button"
          variant="outlined"
        >
          {t($ => $.common.actions.cancel)}
        </Button>
        {!isSendingOtp && otpResendCounter === 0 && !hideResend &&
          <>
            <HSpacer size="4" />
            <Button
              disabled={isAuthenticating}
              onClick={() => {
                sendOtpCode();
              }}
              testID="resend-otp-counter"
            >
              {t($ => $.auth.otp.resend)}
            </Button>
          </>
        }
      </Stack>
    </Stack>
  );
};

export default OtpForm;
